import { pastDueDateMessage } from "encamp-compliance-engine/src/schemas/general/submissionReviewerMessages";
import { BOLD_SIGN_DOCUMENT_EXPIRY_DAYS } from "encamp-shared/src/constants/boldSign";
import { SignatureType, SubmissionDocumentType } from "generated-graphql/graphql";
import { DateTime } from "luxon";

// No schema rule enforces this: before a request is sent the expiry is an
// estimate, and it moves every time the document is re-prepared.
export const AFTER_SIGNATURE_EXPIRY_WARNING = `Signer due date is after the signature request expires (${BOLD_SIGN_DOCUMENT_EXPIRY_DAYS} days from when it is sent)`;

// Due dates are compared in UTC, so an evening in the Americas reads today's date as
// past. Every surface where a due date is set explains that, from one place.
export function utcDueDateTooltip(today: DateTime): string {
  return `Due dates are compared in UTC. Today's UTC date is ${today.toFormat("MMM d, yyyy")}, so that is the earliest allowed date.`;
}

export function earliestAllowedDueDateHint(today: DateTime): string {
  return `Earliest allowed: ${today.toFormat("MMM d, yyyy")} (UTC)`;
}

type SubmissionDocumentLike = {
  type?: SubmissionDocumentType | null;
  externalDocumentExpiresAt?: string | null;
};

/**
 * Expiry of the submission document's signature request, or null when the
 * submission has no digital signature to expire. Falls back to the expiry a request
 * sent today would get, since BoldSign only starts the clock at send and dates are
 * set in the same pass as sending.
 */
export function signatureRequestExpiry(
  documents: readonly SubmissionDocumentLike[],
  today: DateTime,
  signatureType: SignatureType | null | undefined
): DateTime | null {
  if (signatureType !== SignatureType.Digital) {
    return null;
  }

  const expiry = documents.find(
    (d) => d.type === SubmissionDocumentType.Submission && d.externalDocumentExpiresAt
  )?.externalDocumentExpiresAt;

  const parsed = expiry ? DateTime.fromISO(expiry).toUTC().startOf("day") : null;
  return parsed?.isValid ? parsed : today.plus({ days: BOLD_SIGN_DOCUMENT_EXPIRY_DAYS });
}

/**
 * Warns about due dates that still save cleanly but can't be met — a date that has
 * drifted into the past, or a signer deadline later than the expiry of the
 * signature request they're meant to sign.
 */
export function dueDateWarning({
  dueDate,
  isSigner,
  signatureRequestExpiresAt,
  today,
}: {
  dueDate: DateTime;
  isSigner: boolean;
  signatureRequestExpiresAt: DateTime | null;
  today: DateTime;
}): string | undefined {
  if (dueDate < today) {
    return pastDueDateMessage(isSigner);
  }

  if (isSigner && signatureRequestExpiresAt && dueDate > signatureRequestExpiresAt) {
    return AFTER_SIGNATURE_EXPIRY_WARNING;
  }

  return undefined;
}
